import { get } from 'lodash';
import { trackConfig, type IFramework } from '@/framework';
import { checkIsHorizontalMultiLine, TextMeasurer } from '@/utils/text-kit';
import { LayerType, TextWritingType, type ILayerProps, type ITextLayerProps } from '@/version-types';
import { pushHistory, updateGroupLayer, updateLayer, validateInputLayer } from '../utils';
import { type IShapeText } from './shape-text';

export interface IWritingMode {
    /**
     * 设置文字方向
     * @param layerID - 图层 id
     * @param writingMode - 文字方向
     * @returns
     */
    setWritingMode(layerID: string, writingMode: TextWritingType): Promise<ILayerProps | void>;
}

export class WritingMode implements IWritingMode {
    constructor(
        private framework: IFramework,
        private shapeText: IShapeText,
    ) {}

    private calcLayout(prevLayer: ILayerProps, nextLayer: ILayerProps) {
        const prevLayerData = prevLayer.data as ITextLayerProps;
        const nextLayerData = nextLayer.data as ITextLayerProps;

        const measureOptions = {
            keepConstantWidth: false,
            keepConstantHeight: false,
        };

        if (
            nextLayerData.writingMode === TextWritingType.HORIZONTAL_TB &&
            (get(nextLayer, '__fixedDirectionSize', false) ||
                checkIsHorizontalMultiLine(prevLayer.height, prevLayerData.fontSize, prevLayerData.lineSpacing)) // ATTENTION: checkIsHorizontalMultiLine 这里需要使用 prevLayer 数据
        ) {
            measureOptions.keepConstantWidth = true;
        }

        const textSize = TextMeasurer.measure(nextLayer, measureOptions);

        const nextLayerWidth = textSize.width;
        const nextLayerHeight = textSize.height;

        return {
            width: nextLayerWidth,
            height: nextLayerHeight,
        };
    }

    private async set(
        prevLayer: ILayerProps,
        writingMode: TextWritingType,
        options: { update?: boolean; writeHistory?: boolean } = {},
    ) {
        const { update = false, writeHistory = false } = options;

        const { width: prevLayerWidth, height: prevLayerHeight } = prevLayer;
        const prevLayerData = prevLayer.data as ITextLayerProps;
        const nextLayerData: ITextLayerProps = { ...prevLayerData, writingMode };

        let nextLayer: ILayerProps = { ...prevLayer, data: nextLayerData };

        const { shapeExtension } = prevLayerData;
        if (!shapeExtension) {
            // 交换 width 和 height
            nextLayer.width = prevLayerHeight;
            nextLayer.height = prevLayerWidth;

            const { width: nextLayerWidth, height: nextLayerHeight } = this.calcLayout(prevLayer, nextLayer);
            nextLayer.width = nextLayerWidth;
            nextLayer.height = nextLayerHeight;
        } else {
            const layer = await this.shapeText.refreshLayerScreenshotURL(nextLayer);
            if (layer) nextLayer = layer;
        }

        updateLayer(this.framework, nextLayer);
        if (writeHistory)
            pushHistory(this.framework, prevLayer, nextLayer, trackConfig[LayerType.TEXT_LAYER].writingMode);

        return nextLayer;
    }

    async setWritingMode(layerID: string, writingMode: TextWritingType) {
        const prevLayer = this.framework.getLayer(layerID, true);
        if (!validateInputLayer(prevLayer)) return;

        if (prevLayer.layerType === LayerType.GROUP_LAYER) {
            const children = prevLayer.children;
            if (children.length === 0) return;

            const layers = await Promise.all(children.map((child) => this.set(child, writingMode)));
            const nextLayer = updateGroupLayer(this.framework, prevLayer, layers, {
                funcID: trackConfig[LayerType.TEXT_LAYER].writingMode,
                update: true,
                writeHistory: true,
            });

            return nextLayer;
        }

        return this.set(prevLayer, writingMode, { update: true, writeHistory: true });
    }
}
